import { useTemplatePromise } from '../use-template-promise'
import { h, getCurrentInstance, render as vRender, inject, nextTick } from 'vue'
import type { ComponentInternalInstance } from 'vue'
import {
  ElDialog,
  ElButton,
  configProviderContextKey,
  ElConfigProvider,
} from 'element-plus'
import type { VNode } from 'vue'
import type { DialogProps } from 'element-plus'
import { createNameSpace } from '@el-plus/utils/bem'
import { prepareProps } from '@el-plus/utils/props'
import { useLocale } from '../use-locale'

export type DialogOptions = Partial<
  DialogProps & {
    instance: ComponentInternalInstance | null
    render: (props) => VNode
    scopedSlots: Record<string, (props) => VNode>
    class: string
    templatePromiseClass: string
    onCancel: (reject) => void
    onBeforeCancel: (reject) => void
    cancelText?: string
    onConfirm: (resolve) => void
    onBeforeConfirm: (resolve) => void
    confirmText?: string
    showFooter: boolean
  }
>
export type ExpandDialogOptions = {
  title?: string
  dialogProps?: DialogOptions
}
const bem = createNameSpace('dialog')

export const useDialog = (options: DialogOptions = {}) => {
  const { t } = useLocale()
  const {
    scopedSlots = {},
    instance,
    render,
    destroyOnClose,
    onConfirm,
    onBeforeConfirm,
    confirmText,
    onCancel,
    onBeforeCancel,
    cancelText,
    showFooter = true,
    templatePromiseClass,
    ...dialogProps
  } = options

  const Dialog = useTemplatePromise<any>({
    destroy: destroyOnClose,
    class: templatePromiseClass,
    render: (promiseOptions, ctx) => {
      // 插槽参数增加promiseOptions
      for (const key in scopedSlots) {
        scopedSlots[key] = scopedSlots[key].bind(null, promiseOptions)
      }
      const { resolve, reject, isResolving } = promiseOptions
      const renderFunc = () => {
        return render
          ? render(promiseOptions)
          : ctx.slots.default?.(promiseOptions)
      }
      const slots: DialogOptions['scopedSlots'] = {}
      if (showFooter) {
        slots.footer = () => (
          <div class={bem.e('footer')}>
            {scopedSlots.footerPrepend?.(promiseOptions)}
            <ElButton
              size="default"
              onClick={async () => {
                if (onBeforeCancel) {
                  await onBeforeCancel(reject)
                }
                if (onCancel) {
                  onCancel(reject)
                } else {
                  reject('cancel')
                }
              }}
            >
              {cancelText || t('ep.dialog.cancel')}
            </ElButton>
            {scopedSlots.footerInner?.(promiseOptions)}
            <ElButton
              type="primary"
              onClick={async () => {
                if (onBeforeConfirm) {
                  await onBeforeConfirm(resolve)
                }
                if (onConfirm) {
                  onConfirm(resolve)
                } else {
                  resolve('confirm')
                }
              }}
              loading={isResolving}
            >
              {confirmText || t('ep.dialog.confirm')}
            </ElButton>
            {scopedSlots.footerAppend?.(promiseOptions)}
          </div>
        )
      }
      return (
        <ElDialog
          class={bem.b()}
          modelValue={true}
          {...{
            closeOnClickModal: false,
            closeOnPressEscape: false,
            title: t('ep.dialog.title'),
            center: false,
            // 'append-to-body': true,
            beforeClose: () => {
              reject('cancel')
            },
            ...prepareProps(dialogProps, ['modelValue']),
          }}
          v-slots={{
            ...slots,
            ...scopedSlots,
          }}
        >
          {renderFunc()}
        </ElDialog>
      )
    },
  })
  if (render) {
    // const app = createApp(component)
    // Object.assign(app._context, parentInstance.appContext)
    // app.mount(container)
    const container = document.createElement('div')
    // container.setAttribute('id', 'ep-dialog-container')
    const newInstance = instance || getCurrentInstance()
    const config = inject(configProviderContextKey) as any
    // const parentConfig =
    //   newInstance?.provides[configProviderContextKey] ||
    //   newInstance?.appContext?.provides[configProviderContextKey]
    // const vnode = h(Dialog)
    const vnode = h(
      ElConfigProvider,
      { locale: config?.locale },
      { default: () => h(Dialog) },
    )
    if (newInstance && newInstance.appContext) {
      // 强制继承父应用的全局组件、指令和插件
      vnode.appContext = newInstance.appContext
    }
    nextTick(() => {
      vRender(vnode, container)
      document.body.appendChild(container)
    })
  }

  return Dialog
}
